"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, MessageSquare, PhoneCall, Mail, ArrowRight, Search, MapPin, Sparkles, CircleCheck as CheckCircle2, UserCheck, Zap } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const faqs = [{
  id: "faq-1",
  question: "Why is Local SEO crucial for businesses operating in Wolverhampton?",
  answer: "Wolverhampton and the wider West Midlands represent a highly competitive commercial landscape. Local SEO ensures your business appears at the exact moment nearby customers search for your services on Google Search and Maps. By optimizing for high-intent local queries, I help you capture local market share and drive verified phone calls, walk-ins, and direct business inquiries.",
  category: "Strategy"
}, {
  id: "faq-2",
  question: "Will I work directly with you, or will my account be passed to a junior?",
  answer: "You work directly with me—Yassine Essaadi. Unlike traditional agencies that assign account managers or junior executives, I personally conduct your audit, craft your local strategy, implement technical enhancements, and track performance. You receive direct consultant access and honest communication at every step.",
  category: "Direct Consultant"
}, {
  id: "faq-3",
  question: "How quickly can I expect to see my Google Map Pack rankings improve?",
  answer: "Most local clients begin seeing measurable movements in Google Map Pack impressions and local organic rankings within 60 to 90 days. However, Local SEO is a compounding investment: sustainable top rankings require structured Google Business Profile optimization, local citation cleanup, authority building, and authentic review velocity.",
  category: "Results & Timeline"
}, {
  id: "faq-4",
  question: "How do you optimize my Google Business Profile (GBP) for maximum local visibility?",
  answer: "I execute a comprehensive Google Business Profile optimization that includes primary and secondary category alignment, geotagged media integration, structured local service offerings, regular Google Posts, Q&A seeding, and custom spam fighting against illegitimate local competitors.",
  category: "GBP Optimization"
}, {
  id: "faq-5",
  question: "What is the key difference between Local SEO and National SEO?",
  answer: "Local SEO focuses on capturing geographically constrained searches (e.g., 'SEO consultant Wolverhampton' or 'plumber near me') relying heavily on Google Maps, local citations, and proximity signals. National SEO focuses on ranking broad terms across entire countries without geographic parameters.",
  category: "Strategy"
}, {
  id: "faq-6",
  question: "Do I need a physical storefront in Wolverhampton to rank in the local map pack?",
  answer: "No. You can rank effectively as a Service Area Business (SAB) if you serve customers across Wolverhampton, Bilston, Tettenhall, or surrounding West Midlands areas. I set up proper service area parameters without exposing private residential addresses.",
  category: "Technical SEO"
}, {
  id: "faq-7",
  question: "How do local citations and NAP consistency impact my search visibility?",
  answer: "NAP (Name, Address, Phone Number) consistency across authoritative UK directories validates your business legitimacy to Google. I manually audit, clean up duplicate records, and build accurate local citations on trustworthy industry and UK regional platforms.",
  category: "Citations & Trust"
}, {
  id: "faq-8",
  question: "Can Local SEO help me win clients from surrounding areas like Dudley and Walsall?",
  answer: "Yes. Through localized landing pages, strategic service area schema markup, and localized content hubs, I help expand your geographic relevance across adjacent Black Country locations including Dudley, Walsall, and Stourbridge.",
  category: "Geographic Reach"
}, {
  id: "faq-9",
  question: "How do you integrate AI Search Optimisation (GEO) into local search strategies?",
  answer: "Modern search engines use AI overviews and conversational answers (ChatGPT, Gemini, Google AI Overviews). I optimize your entity profiles, structured schema markup, and review sentiment so AI engines recognize your business as the authoritative local recommendation in Wolverhampton.",
  category: "AI Search"
}, {
  id: "faq-10",
  question: "What is included in your Wolverhampton Local SEO audit?",
  answer: "My in-depth local audit evaluates Google Business Profile health, local pack ranking grids, technical site crawl errors, NAP consistency, local competitor backlink gaps, page speed, mobile UX, and conversion rate bottlenecks.",
  category: "Audits"
}, {
  id: "faq-11",
  question: "How do you measure and report ROI from local search campaigns?",
  answer: "I focus on business growth metrics: tracked phone calls, form completions, map direction requests, organic traffic growth, and conversion rates. Reports are clear, transparent, and jargon-free.",
  category: "Reporting & ROI"
}, {
  id: "faq-12",
  question: "How do I get started with your local SEO consultancy services?",
  answer: "Simply book a free, no-obligation Local SEO Consultation. We will review your current online presence in Wolverhampton, discuss your business goals, and determine the exact roadmap needed for long-term search growth.",
  category: "Getting Started"
}];
const categories = ["All", "Strategy", "Direct Consultant", "GBP Optimization", "AI Search", "Reporting & ROI"];
export default function Localseofaq() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = activeCategory === "All" || faq.category === activeCategory;
    const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) || faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });
  const column1Faqs = filteredFaqs.filter((_, idx) => idx % 2 === 0);
  const column2Faqs = filteredFaqs.filter((_, idx) => idx % 2 === 1);
  return <section id="localseofaq" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-96 bg-secondary/30 blur-3xl pointer-events-none rounded-lg" />
      <div className="absolute bottom-10 right-10 size-80 bg-accent/10 blur-3xl pointer-events-none rounded-lg" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        
        <div className="mx-auto max-w-3xl text-center">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="flex items-center justify-center gap-2 mb-4">
            <Badge variant="outline" className="px-3 py-1 text-xs font-medium border-primary/20 bg-primary/5 text-primary">
              <MapPin className="size-3.5 mr-1 text-primary" />
              Wolverhampton & West Midlands
            </Badge>
            <Badge variant="secondary" className="px-3 py-1 text-xs font-medium">
              Direct Consultant Access
            </Badge>
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="font-serif text-2xl font-bold tracking-tight text-foreground sm:text-3xl lg:text-4xl">
            Local SEO Questions & Clear Answers
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2,
          ease: "easeOut"
        }} className="mt-4 text-base text-muted-foreground sm:text-lg leading-relaxed">
            Everything you need to know about growing your local search presence in Wolverhampton. 
            No jargon, no agency account managers—just direct, transparent answers from an independent SEO consultant.
          </motion.p>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.25,
        ease: "easeOut"
      }} className="mt-10 flex flex-col md:flex-row items-center justify-between gap-4 bg-card p-4 rounded-xl border border-border shadow-xs">
          <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
            {categories.map((cat, idx) => <Button key={cat} data-index={idx} variant={activeCategory === cat ? "default" : "ghost"} size="sm" onClick={() => setActiveCategory(cat)} className={`rounded-lg text-xs font-medium transition-[color,background-color] ${activeCategory === cat ? "bg-primary text-primary-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"}`}>
                {cat}
              </Button>)}
          </div>

          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <input type="text" placeholder="Search local questions..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full rounded-md border border-input bg-background pl-9 pr-4 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all" />
          </div>
        </motion.div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          
          <div className="flex flex-col gap-4">
            <Accordion type="single" collapsible className="w-full flex flex-col gap-4">
              {column1Faqs.map((faq, i) => <motion.div key={faq.id} data-index={i} initial={{
              opacity: 0,
              y: 15
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.05,
              ease: "easeOut"
            }}>
                  <AccordionItem value={faq.id} className="bg-card border border-border rounded-xl px-5 py-1 shadow-xs hover:border-primary/30 transition-[border-color,box-shadow]">
                    <AccordionTrigger className="text-left font-semibold text-primary hover:no-underline text-base sm:text-lg py-4">
                      <span className="flex items-start gap-3">
                        <HelpCircle className="size-5 text-primary shrink-0 mt-0.5" />
                        <span>{faq.question}</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-1 pb-5 pl-8">
                      {faq.answer}
                      <div className="mt-3 flex items-center gap-2 pt-2 border-t border-border/50">
                        <Badge variant="outline" className="text-[11.5px] text-muted-foreground border-border">
                          Category: {faq.category}
                        </Badge>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>)}
            </Accordion>
          </div>

          <div className="flex flex-col gap-4">
            <Accordion type="single" collapsible className="w-full flex flex-col gap-4">
              {column2Faqs.map((faq, i) => <motion.div key={faq.id} data-index={i} initial={{
              opacity: 0,
              y: 15
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.05 + 0.1,
              ease: "easeOut"
            }}>
                  <AccordionItem value={faq.id} className="bg-card border border-border rounded-xl px-5 py-1 shadow-xs hover:border-primary/30 transition-[border-color,box-shadow]">
                    <AccordionTrigger className="text-left font-semibold text-primary hover:no-underline text-base sm:text-lg py-4">
                      <span className="flex items-start gap-3">
                        <HelpCircle className="size-5 text-primary shrink-0 mt-0.5" />
                        <span>{faq.question}</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-1 pb-5 pl-8">
                      {faq.answer}
                      <div className="mt-3 flex items-center gap-2 pt-2 border-t border-border/50">
                        <Badge variant="outline" className="text-[11.5px] text-muted-foreground border-border">
                          Category: {faq.category}
                        </Badge>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>)}
            </Accordion>
          </div>

        </div>

        {filteredFaqs.length === 0 && <div className="text-center py-16 bg-card border border-border rounded-xl mt-6">
            <HelpCircle className="mx-auto size-12 text-muted-foreground/60 mb-3" />
            <h3 className="text-lg font-semibold text-foreground">No matching questions found</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
              I am happy to answer any specific question about your Wolverhampton business directly.
            </p>
            <Button variant="outline" size="sm" onClick={() => {
          setActiveCategory("All");
          setSearchQuery("");
        }} className="mt-4">
              Reset Filters
            </Button>
          </div>}

        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="mt-16 bg-card border border-primary/20 rounded-2xl p-8 sm:p-10 shadow-lg relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-lg blur-2xl pointer-events-none" />
          
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8 relative z-10">
            <div className="space-y-3 text-center lg:text-left max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-primary/10 text-primary text-xs font-semibold">
                <Sparkles className="size-3.5" />
                <span>Direct Expert Support</span>
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl font-bold text-foreground">
                Have a specific local SEO question about your Wolverhampton business?
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base">
                Book a direct, strategy-first consultation with me. No pressure, no sales team—just actionable insights tailored to your business goals.
              </p>
              
              <div className="pt-2 flex flex-wrap items-center justify-center lg:justify-start gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="size-4 text-primary" /> 100% Direct Consultant Access
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="size-4 text-primary" /> Transparent SEO Audits
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="size-4 text-primary" /> Tailored Strategy
                </span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row lg:flex-col gap-3 w-full sm:w-auto shrink-0">
              <Button asChild size="lg" className="w-full sm:w-auto shadow-md">
                <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                  <span>Book a Free Consultation</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                <Link to="/LocalSEOPage" className="inline-flex items-center justify-center gap-2">
                  <MapPin className="size-4 text-primary" />
                  <span>Explore Local SEO Services</span>
                </Link>
              </Button>
            </div>
          </div>

          <Separator className="my-8" />

          <div className="flex flex-col sm:flex-row items-center justify-between text-xs text-muted-foreground gap-4">
            <div className="flex items-center gap-6">
              <a href="mailto:contact@ye-consultancy.com" className="inline-flex items-center gap-1.5 hover:text-primary transition-colors">
                <Mail className="size-3.5 text-primary" />
                <span>contact@ye-consultancy.com</span>
              </a>
              <a href="tel:+447490984430" className="inline-flex items-center gap-1.5 hover:text-primary transition-colors">
                <PhoneCall className="size-3.5 text-primary" />
                <span>+44 7490984430</span>
              </a>
            </div>
            
            <p className="text-center sm:text-right">
              Windmill Hill Business Park, Whitehill Way, Swindon SN5 6QR
            </p>
          </div>
        </motion.div>

      </div>
    </section>;
}